import type { Graph, Product } from 'schema-dts'
import type { EventDescription } from '~/composables/types/event'
import type { ShippingCost } from '~/composables/types/cart'
import { organizationReference } from '~/composables/schemaOrg/useOrganizationSchema'
import { getKitReturnPolicy, getKitShippingDetails } from '~/utils/kitSchema'

const useKitProductSchema = (
  kits?: MaybeRefOrGetter<EventDescription[] | undefined | null>,
  { shippingCosts = [] }: { shippingCosts?: ShippingCost[] } = {}
): any => {
  const schema = ref<Graph | undefined>(undefined)
  const script = computed(() => ({
    type: 'application/ld+json',
    innerHTML: schema.value,
  }))
  const fullLocalizedUrl = useFullLocalizedUrl()

  const createSchema = (
    kitList: EventDescription[] | undefined | null
  ): Product[] => {
    if (!kitList || !Array.isArray(kitList)) {
      return []
    }

    const shippingDetails = getKitShippingDetails(shippingCosts)
    const returnPolicy = getKitReturnPolicy(shippingCosts)

    return kitList.map((kit) => {
      const reviewSchemaArr = eventGetters.getReviewsList(kit).map((review) => {
        return {
          '@type': 'Review',
          reviewRating: {
            '@type': 'Rating',
            ratingValue: reviewGetters.getReviewRating(review),
          },
          reviewBody: reviewGetters.getReview(review),
          author: {
            '@type': 'Person',
            name: reviewGetters.getAuthor(review),
          },
        }
      })

      const galleryImages = eventGetters.getSchemaImages(kit)
      const images = galleryImages.length
        ? galleryImages
        : [kit.main_image?.thumb].filter(Boolean)
      const url = fullLocalizedUrl(`/k/${eventGetters.getPermalink(kit)}/`)

      const obj = {
        '@type': 'Product',
        '@id': `${url}#product`,
        name: eventGetters.getTitle(kit),
        url,
        image: images,
        description: eventGetters.getSubtitle(kit),
        offers: {
          '@type': 'Offer',
          availability: getSchemaOfferAvailability(kit.total_tickets_available),
          url,
          price: eventGetters.getDefaultPriceAmount(kit) / 100,
          priceCurrency: eventGetters.getDefaultPriceCurrency(kit),
          priceSpecification: eventGetters.getSchemaPriceSpecification(kit),
          seller: organizationReference,
          ...(shippingDetails.length && { shippingDetails }),
          ...(returnPolicy && { hasMerchantReturnPolicy: returnPolicy }),
        },
        brand: {
          '@type': 'Brand',
          name:
            supplierGetters.getName(eventGetters.getSupplier(kit)) ||
            kit?.supplier,
          url: fullLocalizedUrl(
            `/p/${supplierGetters.getPermalink(eventGetters.getSupplier(kit))}`
          ),
        },
        ...(eventGetters.getReviewsTotal(kit) &&
          eventGetters.getReviewsTotal(kit) > 0 && {
            review: reviewSchemaArr,
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: eventGetters.getReviewsAverage(kit, 'en-US'),
              bestRating: 5,
              worstRating: 1,
              ratingCount: eventGetters.getReviewsTotal(kit),
            },
          }),
      }

      return obj
    })
  }

  schema.value = {
    '@context': 'https://schema.org',
    '@graph': createSchema(kits),
  }

  return {
    schema,
    script,
  }
}

export default useKitProductSchema
